import { A, BACKSPACE, DASH, DELETE, NUMPAD_MINUS, NUMPAD_PLUS, Z } from '@angular/cdk/keycodes';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { IPoint } from '@foblex/2d';
import {
  EFMarkerType,
  FCanvasComponent,
  FConnectionContent,
  FCreateConnectionEvent,
  FDragStartedEvent,
  FFlowComponent,
  FFlowModule,
  FSelectionChangeEvent,
  FZoomDirective,
  injectFlowState
} from '@foblex/flow';
import { EOperationSystem, PlatformService } from '@foblex/platform';
import { MouseTrackerDirective } from '@iot-platform/directives';
import { OrderByPipe } from '@iot-platform/pipes';
import { TranslatePipe } from '@ngx-translate/core';
import { FlowActionPanelComponent } from './components/flow-action-panel/flow-action-panel.component';
import { FlowGroupComponent } from './components/flow-group/flow-group.component';
import { FlowInputComponent } from './components/flow-input/flow-input.component';
import { FlowNodeComponent } from './components/flow-node/flow-node.component';
import { FlowOutputComponent } from './components/flow-output/flow-output.component';
import { FlowActionPanelAction, FlowNodeType, FlowZoomAction } from './enums';
import { FlowConfig, FlowDispatchEvent, FlowNode, FlowState, FlowStateConnection } from './models';
import { ViewPortType } from './models/flow-port.model';
import { FlowApiService } from './services/flow-api.service';
import { FlowDragAndDropService } from './services/flow-drag-and-drop.service';
import { FlowZoomService } from './services/flow-zoom.service';

@Component({
  selector: 'iot-platform-ui-flow',
  templateUrl: './flow.component.html',
  styleUrls: ['./flow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FFlowModule,
    FlowActionPanelComponent,
    FormsModule,
    MatProgressSpinnerModule,
    FlowGroupComponent,
    FConnectionContent,
    MatIcon,
    MatIconButton,
    MatTooltip,
    MouseTrackerDirective,
    FlowOutputComponent,
    FlowInputComponent,
    FlowNodeComponent,
    FlowActionPanelComponent,
    OrderByPipe,
    NgClass,
    MatButton,
    TranslatePipe
  ],
  providers: [FlowDragAndDropService],
  host: {
    '(keydown)': 'onKeyDown($event)',
    tabindex: '-1'
  }
})
export class FlowComponent implements OnDestroy {
  public readonly flow = model.required<FlowState | null>();
  public readonly config = input<FlowConfig | null>();
  public readonly loading = input<boolean>(false);
  public readonly canConfigure = input<boolean>(true);
  ViewPortType = ViewPortType;
  dispatchEvent = output<FlowDispatchEvent>();
  actions = computed(() => this.config()?.actions ?? []);
  actionMap = computed(() => new Set(this.actions()));
  readonly eMarkerType = EFMarkerType;
  readonly fCanvasChangeEventDebounce = 200;
  dragEndInProgress = signal(false);
  /** Focus mode state, read by the template to render the "back to the full flow" overlay button. */
  readonly flowZoom = inject(FlowZoomService);
  private readonly state = injectFlowState<FlowNode, FlowStateConnection, FlowNode>();
  readonly basicNodes = this.state.nodes;
  readonly groupNodes = this.state.groups;
  readonly connections = this.state.connections;
  readonly transform = this.state.transform;
  private readonly apiService = inject(FlowApiService);
  private readonly dragService = inject(FlowDragAndDropService);
  private readonly platform = inject(PlatformService);
  private readonly elementRef = inject(ElementRef);
  private readonly flowComponent = viewChild(FFlowComponent);
  private readonly canvasComponent = viewChild(FCanvasComponent);
  private readonly zoomDirective = viewChild(FZoomDirective);

  constructor() {
    effect(() => {
      const flow = this.flow();
      untracked(() => {
        this.load(flow);
      });
    });
  }

  /** Teardown only: never reload / fit the canvas here, Foblex would hook onDestroy on a destroyed view (NG0911). */
  ngOnDestroy() {
    this.flowComponent()?.clearSelection?.();
    this.flowComponent()?.reset?.();
    this.flow.set(null);
  }

  actualClick(node: FlowNode) {
    const dragEndInProgress = this.dragEndInProgress();
    if (!dragEndInProgress) {
      this.dispatchEvent.emit({
        type: FlowActionPanelAction.NODE_CLICKED,
        node
      });
    }
    this.dragEndInProgress.set(false);
  }

  addNode(node: FlowNode) {
    this.dispatchEvent.emit({ type: FlowActionPanelAction.ADD_NODE, node });
  }

  /**
   * Leaves the focus mode. Canvas-level action: it carries no node, the consumer owning the zoom
   * state is the one that knows which graph to restore.
   */
  exitZoom(): void {
    this.dispatchEvent.emit({ type: FlowZoomAction.EXIT_ZOOM });
  }

  reset(): void {
    this.flowComponent()?.clearSelection?.();
    this.flowComponent()?.reset?.();
    this.load(this.flow());
  }

  /** Bound to `fFullRendered` (also re-fired by Foblex on undo back to the loaded state): fit without animation. */
  editorLoaded(): void {
    this.fitToScreen();
  }

  fitToScreen(options: Partial<{ padding: IPoint; animated: boolean }> = {}): void {
    const { padding = { x: 50, y: 50 }, animated = true } = options;
    this.canvasComponent()?.fitToScreen(padding, animated);
  }

  onDragStarted(event: FDragStartedEvent): void {
    this.dragService.onDragStarted(event);
    this.dispatchEvent.emit({
      type: FlowActionPanelAction.DRAG_START,
      options: {
        ...event
      }
    });
  }

  onDragEnded(): void {
    this.dragEndInProgress.set(true);
    this.dragService.onDragEnded();
    this.dispatchEvent.emit({ type: FlowActionPanelAction.DRAG_END });
    setTimeout(() => {
      // Workaround to prevent conflict between drag end and node clicked events
      this.dragEndInProgress.set(false);
    }, 200);
  }

  updateMouseTrackerPosition(): void {
    const flow = this.flowComponent();
    if (!flow) {
      return;
    }
    this.dragService.updateMouseTrackerPosition(flow, this.elementRef);
  }

  createConnection(event: FCreateConnectionEvent): void {
    this.dispatchEvent.emit({
      type: FlowActionPanelAction.CREATE_CONNECTION,
      options: {
        ...event
      }
    });
  }

  /**
   * Template listeners run before the Foblex auto-wiring: apply the selection (idempotent) so the
   * consumers reading the state on CHANGE_SELECTION see the new one.
   */
  changeSelection(event: FSelectionChangeEvent): void {
    this.state.applySelectionChange(event);
    this.dispatchEvent.emit({ type: FlowActionPanelAction.CHANGE_SELECTION });
  }

  removeConnection(outputId: string): void {
    this.apiService.removeConnection(outputId);
  }

  onDispatchEvent(event: FlowDispatchEvent): void {
    this.dispatchEvent.emit(event);
  }

  processAction(event: FlowActionPanelAction): void {
    switch (event) {
      case FlowActionPanelAction.SELECT_ALL:
        this.selectAll();
        break;
      case FlowActionPanelAction.DESELECT_ALL:
        this.deselectAll();
        break;
      case FlowActionPanelAction.ZOOM_IN:
        this.zoomDirective()?.zoomIn();
        break;
      case FlowActionPanelAction.ZOOM_OUT:
        this.zoomDirective()?.zoomOut();
        break;
      case FlowActionPanelAction.FIT_TO_SCREEN:
        this.fitToScreen();
        break;
      case FlowActionPanelAction.ONE_TO_ONE:
        this.canvasComponent()?.resetScaleAndCenter();
        break;
      case FlowActionPanelAction.RESET_POSITIONS:
        this.onDispatchEvent({
          type: FlowActionPanelAction.RESET_POSITIONS
        });
        break;
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    const isCommand = this.isCommandButton(event);
    const actionMap = this.actionMap();
    switch (event.keyCode) {
      case BACKSPACE:
      case DELETE:
        if (actionMap?.has(FlowActionPanelAction.REMOVE_NODE)) {
          this.apiService.removeSelected();
        }
        break;
      case NUMPAD_PLUS:
        if (isCommand && actionMap?.has(FlowActionPanelAction.ZOOM_IN)) {
          this.zoomDirective()?.zoomIn();
          event.preventDefault();
        }
        break;
      case NUMPAD_MINUS:
      case DASH:
        if (isCommand && actionMap?.has(FlowActionPanelAction.ZOOM_OUT)) {
          this.zoomDirective()?.zoomOut();
          event.preventDefault();
        }
        break;
      case A:
        if (isCommand && actionMap?.has(FlowActionPanelAction.SELECT_ALL)) {
          this.selectAll();
          event.preventDefault();
        }
        break;
      case Z:
        if (isCommand && actionMap?.has(FlowActionPanelAction.UNDO)) {
          this.state.undo();
          event.preventDefault();
        }
        break;
    }
  }

  /** Single `load()` of the graph into the Foblex state (nodes, groups and connections together). */
  private load(flow: FlowState | null): void {
    const nodes = Object.values(flow?.nodes ?? {});
    this.state.load({
      nodes: nodes.filter((n) => !this.isGroup(n)),
      groups: nodes.filter((n) => this.isGroup(n)),
      connections: Object.values(flow?.connections ?? {}).map((c) => ({ ...c, sourceId: c.sourcePortId, targetId: c.targetPortId })),
      selection: { nodeIds: flow?.selection?.nodes ?? [], groupIds: [], connectionIds: flow?.selection?.connections ?? [] },
      transform: flow?.transform
    });
    this.fitToScreen({ animated: false });
  }

  private isGroup(node: FlowNode): boolean {
    return node.type === FlowNodeType.GROUP;
  }

  private selectAll(): void {
    this.flowComponent()?.selectAll();
    this.apiService.selectAll();
  }

  private deselectAll(): void {
    this.flowComponent()?.clearSelection();
    this.apiService.deselectAll();
    this.dispatchEvent.emit({ type: FlowActionPanelAction.DESELECT_ALL });
  }

  private isCommandButton(event: KeyboardEvent): boolean {
    return this.platform.getOS() === EOperationSystem.MAC_OS ? event.metaKey : event.ctrlKey;
  }
}
